import * as XLSX from 'xlsx';
import React, { useEffect, useState, useRef } from 'react';
import Sidebar from '../Components/Sidebar';
import { MdArrowDropDown } from "react-icons/md";
import excel from '../assests/images/excel img.png';
import CreateAffiliateform from './CreateAffiliateform';
import AffiliateEditform from './AffiliateEditform';
import Constant from '../Components/Utils/Constant';
import axios from 'axios';
import ReactPaginate from 'react-paginate';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import EditAffiliateForm from '../Components/models/EditAffiliateForm';
import { useSelector } from 'react-redux';

export default function Affiliate() {
  const [showAffiliateForm, setShowAffiliateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showBlockConfirmation, setShowBlockConfirmation] = useState(false);
  const [showInactiveConfirmation, setShowInactiveConfirmation] = useState(false);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [affiliate, setAffiliate] = useState([]);
  const tableRef = React.createRef();
  const [editPopup, setEditPopup] = useState(false);
  const [affiliateToDelete, setAffiliateToDelete] = useState(null);
  const highlightedId = useSelector(state => state.DataReducer.highlightedId);
  const userRes = useSelector(state => state?.DataReducer?.user);

  // Filters and sorts state
  const [selectedCreatedBy, setSelectedCreatedBy] = useState(null);
  const [selectedAffiliateStatus, setSelectedAffiliateStatus] = useState(null);
  const [sortDemo, setSortDemo] = useState(null);   // "asc" | "desc" | null
  const [sortSales, setSortSales] = useState(null); // "asc" | "desc" | null
  const [isDropdownOpen, setIsDropdownOpen] = useState(null);
  const dropdownRef = useRef(null);

  // Block state
  const [affiliateToBlock, setAffiliateToBlock] = useState(null);

  // Fetch data from API with server-side filtering, sorting, and pagination
  const fetchUser = async (page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page });

      if (selectedCreatedBy) {
        params.append('createdBy', selectedCreatedBy);
      }
      if (selectedAffiliateStatus) {
        const status = selectedAffiliateStatus === 'Blocked' ? 'blocked' : 'active';
        params.append('status', status);
      }
      if (sortDemo) {
        params.append('sortBy', 'demo');
        params.append('order', sortDemo);
      }
      if (sortSales) {
        params.append('sortBy', 'sales');
        params.append('order', sortSales);
      }

      const response = await fetch(`${Constant.BASE_URL}/affiliate/get?${params.toString()}`);
      const data = await response.json();

      if (data && data.success) {
        setAffiliate(data.affiliate);
        setTotalCount(data.totalCount);
        setTotalPages(data.totalPages);
        setCurrentPage(page);
      }
    } catch (error) {
      console.error('Error fetching affiliate:', error);
      toast.error("Failed to fetch data.");
    } finally {
      setLoading(false);
    }
  };

  // This effect now triggers a refetch whenever filters, sorting, or page changes
  useEffect(() => {
    fetchUser(currentPage);
  }, [currentPage, selectedCreatedBy, selectedAffiliateStatus, sortDemo, sortSales, showAffiliateForm, editPopup]);

  const handleOptionChange = (event, row) => {
    const selectedValue = event.target.value;
    if (selectedValue === 'Edit') {
      setEditPopup(row);
    } else if (selectedValue === 'Delete') {
      handleOpenDeleteConfirmation(row._id);
    } else if (selectedValue === 'active') {
      handleConfirmation('inactive');
    } else if (selectedValue === 'block') {
      handleBlockConfirmation(row);
    }
    event.target.value = ""; // Reset select to default
  };

  const handleBlockConfirmation = (row) => {
    setAffiliateToBlock(row);
    setShowBlockConfirmation(true);
  };

  const handleConfirmBlock = async () => {
    if (affiliateToBlock) {
      try {
        const newBlockStatus = !affiliateToBlock.block;
        const response = await axios.put(
          `${Constant.BASE_URL}/affiliate/update`,
          { ...affiliateToBlock, block: newBlockStatus }
        );
        if (response.status === 200) {
          setAffiliate((prevAdmins) =>
            prevAdmins.map((item) =>
              item._id === affiliateToBlock._id
                ? { ...item, block: newBlockStatus }
                : item
            )
          );
          const message = newBlockStatus ? 'Blocked' : 'Unblocked';
          toast.success(`Affiliate ${message} successfully!`);
        } else {
          toast.error('Failed to update affiliate status.');
        }
        setShowBlockConfirmation(false);
        setAffiliateToBlock(null);
      } catch (error) {
        toast.error('Failed to update affiliate status.');
      }
    }
  };

  const handleCloseBlockConfirmation = () => {
    setShowBlockConfirmation(false);
    setAffiliateToBlock(null);
  };

  const handleExportToExcel = () => {
    if (tableRef.current) {
      const ws = XLSX.utils.table_to_sheet(tableRef.current);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet 1');
      XLSX.writeFile(wb, 'tablexls.xlsx');
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handlePageClick = ({ selected }) => {
    const newPageNumber = selected + 1;
    setCurrentPage(newPageNumber); // This will trigger the useEffect to refetch
  };

  const handleOpenDeleteConfirmation = (affiliateId) => {
    setAffiliateToDelete(affiliateId);
    setShowDeleteConfirmation(true);
  };

  const handleDelete = async () => {
    if (affiliateToDelete) {
      try {
        const response = await axios.delete(`${Constant.BASE_URL}/affiliate/delete/${affiliateToDelete}`);
        if (response.status === 200) {
          toast.success("Affiliate deleted successfully");
          fetchUser(currentPage); // Refetch data for the current page
        } else {
          toast.error("Failed to delete affiliate");
        }
      } catch (error) {
        toast.error("Failed to delete affiliate");
      } finally {
        setAffiliateToDelete(null);
        setShowDeleteConfirmation(false);
      }
    }
  };

  const handleCreateAdminClick = () => setShowAffiliateForm(true);
  const handleClosePopup = () => setShowAffiliateForm(false);
  const handleCloseEdit = () => setShowEditForm(false);

  const handleConfirmation = (confirmationType) => {
    if (confirmationType === 'delete') setShowDeleteConfirmation(true);
    else if (confirmationType === 'block') setShowBlockConfirmation(true);
    else if (confirmationType === 'inactive') setShowInactiveConfirmation(true);
  };

  // Dynamic lists for dropdowns can still be built from the fetched data, or ideally from a separate API endpoint
  const createdByList = [...new Set(affiliate.map(a => a.createdby).filter(Boolean))];
  const affiliateStatusList = ["Active", "Blocked"]; // This can be a static list

  return (
    <Sidebar>
      <>
        <ToastContainer position="top-right" autoClose={3000} />
        <div className="p-3 bg-grey-300 lg:p-3 xl:p-3 2xl:p-6">
          <div className="bg-white h-[78.55vh] overflow-hidden" style={{ borderRadius: '0px 0px 11px 11px' }}>

            {/* Header */}
            <div className="flex justify-between items-center px-4 py-2 bg-white border-b flex-wrap gap-2">
              <h2 className="text-lg font-bold whitespace-nowrap">Top Affiliate: {totalCount}</h2>
              <div className="flex items-center space-x-2 lg:space-x-4">
                <button onClick={handleExportToExcel} className="flex items-center space-x-2">
                  <img src={excel} alt="excel" className="w-6 h-6" />
                  <span className="hidden sm:inline">Export</span>
                </button>
                <div
                  style={{
                    background:
                      "radial-gradient(125% 125% at 85.42% 0%, #FFD6A6 0%, #FF9C28 100%)",
                  }}
                  className="min-w-[130px] px-4 py-2 cursor-pointer flex justify-center items-center rounded-md border border-white border-opacity-60 bg-white bg-opacity-57 backdrop-blur-[5.7px] shadow-md hover:scale-105 transition-transform duration-200"
                >
                  <div
                    onClick={handleCreateAdminClick}
                    className="text-sm lg:text-[10px] xl:text-[15px] 2xl:text-[24px] font-semibold text-center"
                  >
                    <p>Create Affiliate</p>
                  </div>
                </div>


              </div>
            </div>

            {/* Table Container */}
            <div className="w-full overflow-hidden" ref={dropdownRef}>
              <div className="tablebodydata overflow-auto lg:h-[62vh] xl:h-[63vh] 2xl:h-[63vh] relative">
                <table
                  ref={tableRef}
                  className="w-full divide-y divide-gray-200 table-hover table-bordered"
                  id="myTable"
                  style={{ tableLayout: 'fixed', minWidth: '100%' }}
                >
                  <thead className='bg-gray-100 sticky top-0 z-10'>
                    <tr>
                      <th className="px-2 py-2 text-xs lg:text-[10px] xl:text-sm 2xl:text-base" style={{ width: '10%', minWidth: '80px' }}>Action</th>
                      <th className="px-2 py-2 text-xs lg:text-[10px] xl:text-sm 2xl:text-base" style={{ width: '15%', minWidth: '100px' }}>Name</th>
                      <th className="px-2 py-2 text-xs lg:text-[10px] xl:text-sm 2xl:text-base" style={{ width: '12%', minWidth: '90px' }}>Phone</th>
                      <th className="px-2 py-2 text-xs lg:text-[10px] xl:text-sm 2xl:text-base" style={{ width: '18%', minWidth: '120px' }}>Email</th>

                      {/* Created By dropdown */}
                      <th className="relative px-2 py-2 text-xs lg:text-[10px] xl:text-sm 2xl:text-base" style={{ width: '12%', minWidth: '100px' }}>
                        <div onClick={() => setIsDropdownOpen(isDropdownOpen === "createdby" ? null : "createdby")} className="flex items-center justify-center cursor-pointer">
                          <span className="truncate">{selectedCreatedBy || "Created By"}</span>
                          <MdArrowDropDown className="ml-1 flex-shrink-0" />
                        </div>
                        {isDropdownOpen === "createdby" && (
                          <div className="absolute bg-white border mt-1 z-50 left-0 right-0 max-h-40 overflow-y-auto shadow-lg">
                            {createdByList.map(cb => (
                              <div key={cb} className="px-2 py-1 hover:bg-gray-200 cursor-pointer text-xs" onClick={() => { setSelectedCreatedBy(cb); setIsDropdownOpen(null); }}>
                                {cb}
                              </div>
                            ))}
                            <div className="px-2 py-1 text-red-500 cursor-pointer text-xs border-t" onClick={() => { setSelectedCreatedBy(null); setIsDropdownOpen(null); }}>
                              Clear Filter
                            </div>
                          </div>
                        )}
                      </th>

                      {/* Affiliate Status dropdown */}
                      <th className="relative px-2 py-2 text-xs lg:text-[10px] xl:text-sm 2xl:text-base" style={{ width: '12%', minWidth: '100px' }}>
                        <div onClick={() => setIsDropdownOpen(isDropdownOpen === "status" ? null : "status")} className="flex items-center justify-center cursor-pointer">
                          <span className="truncate">{selectedAffiliateStatus || "Status"}</span>
                          <MdArrowDropDown className="ml-1 flex-shrink-0" />
                        </div>
                        {isDropdownOpen === "status" && (
                          <div className="absolute bg-white border mt-1 z-50 left-0 right-0 max-h-40 overflow-y-auto shadow-lg">
                            {affiliateStatusList.map(st => (
                              <div key={st} className="px-2 py-1 hover:bg-gray-200 cursor-pointer text-xs" onClick={() => { setSelectedAffiliateStatus(st); setIsDropdownOpen(null); }}>
                                {st}
                              </div>
                            ))}
                            <div className="px-2 py-1 text-red-500 cursor-pointer text-xs border-t" onClick={() => { setSelectedAffiliateStatus(null); setIsDropdownOpen(null); }}>
                              Clear Filter
                            </div>
                          </div>
                        )}
                      </th>

                      {/* Demo Users ASC/DESC */}
                      <th onClick={() => { setSortSales(null); setSortDemo(sortDemo === "asc" ? "desc" : "asc"); }} className="cursor-pointer px-2 py-2 hover:bg-gray-200 text-xs lg:text-[10px] xl:text-sm 2xl:text-base" style={{ width: '10%', minWidth: '90px' }}>
                        <div className="flex items-center justify-center">
                          <span className="truncate">Demo Users</span>
                          <span className="ml-1">{sortDemo === "asc" ? "↑" : sortDemo === "desc" ? "↓" : "↕"}</span>
                        </div>
                      </th>

                      {/* Sales Users ASC/DESC */}
                      <th onClick={() => { setSortDemo(null); setSortSales(sortSales === "asc" ? "desc" : "asc"); }} className="cursor-pointer px-2 py-2 hover:bg-gray-200 text-xs lg:text-[10px] xl:text-sm 2xl:text-base" style={{ width: '10%', minWidth: '90px' }}>
                        <div className="flex items-center justify-center">
                          <span className="truncate">Sales Users</span>
                          <span className="ml-1">{sortSales === "asc" ? "↑" : sortSales === "desc" ? "↓" : "↕"}</span>
                        </div>
                      </th>
                    </tr>
                  </thead>

                  {/* The table body now maps directly over the 'affiliate' state */}
                  <tbody className="divide-y divide-gray-200">
                    {affiliate.map((row, index) => (
                      <tr key={row._id || index}
                        className={index % 2 === 0 ? 'bg-white' : 'bg-gray-100'}
                        style={{ background: highlightedId === row._id ? "#FFFDE7" : "" }}>

                        <td className="px-2 py-1" style={{ width: '10%' }}>
                          <div className='flex justify-center items-center text-center'>
                            <select
                              className="text-xs py-1 px-1 border rounded shadow-sm w-full outline-none"
                              onChange={(event) => handleOptionChange(event, row)}
                              defaultValue=""
                            >
                              <option value="" disabled>Select</option>
                              <option value="Edit">Edit</option>
                              <option value="Delete">Delete</option>
                              <option value="active">InActive</option>
                              <option value="block">{row.block ? 'Active' : 'Block'}</option>
                            </select>
                          </div>
                        </td>
                        <td className="px-2 py-1 text-xs lg:text-[10px] xl:text-sm" style={{ width: '15%' }}><div className="truncate" title={row.name}>{row.name}</div></td>
                        <td className="px-2 py-1 text-xs lg:text-[10px] xl:text-sm" style={{ width: '12%' }}><div className="truncate" title={row.phone}>{row.phone}</div></td>
                        <td className="px-2 py-1 text-xs lg:text-[10px] xl:text-sm" style={{ width: '18%' }}><div className="truncate" title={row.email}>{row.email}</div></td>
                        <td className="px-2 py-1 text-xs lg:text-[10px] xl:text-sm" style={{ width: '12%' }}><div className="truncate" title={row.createdby}>{row.createdby}</div></td>
                        <td className="px-2 py-1 text-center text-xs lg:text-[10px] xl:text-sm" style={{ width: '12%' }}>
                          {row.block ?
                            <span className="text-red-600">BLOCKED</span> :
                            <span className="text-green-600">Active</span>
                          }
                        </td>
                        <td className="px-2 py-1 text-center text-xs lg:text-[10px] xl:text-sm" style={{ width: '10%' }}>{userRes?.admins?.filter(u => u.createdby === row?.name && u.usertype === "Demo").length || 0}</td>
                        <td className="px-2 py-1 text-center text-xs lg:text-[10px] xl:text-sm" style={{ width: '10%' }}>{userRes?.admins?.filter(u => u.createdby === row?.name && u.usertype === "Sales").length || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          {showAffiliateForm && <CreateAffiliateform onClose={handleClosePopup} />}
          {showEditForm && <AffiliateEditform onClosed={handleCloseEdit} />}
        </div>

        {/* Pagination */}
        <div className="flex lg:px-4">
          <div className="w-full flex bg-white shadow-md backdrop-blur-[5.7px]">
            <div className='w-full flex items-center fixed lg:bottom-5 xl:bottom-6 2xl:bottom-6 border-t lg:text-[10px] xl:text-[14px] 2xl:text-3xl lg:pt-1 xl:pt-1 2xl:pt-0'>
              <div className='flex justify-start w-full xl:px-5 2xl:p-5'>
                <p className="dark:text-gray-400">
                  Showing <span className='text-blue-500'>{(currentPage - 1) * 15 + 1}</span> to{" "}
                  <span className='text-blue-500'>{Math.min(currentPage * 15, totalCount || 0)}</span> of{" "}
                  <span className='text-yellow-500'>{totalCount || 0}</span> entries
                </p>
              </div>
              <div className='flex justify-start pr-2 text-blue-500 pagination xl:pr-5'>
                <ReactPaginate
                  previousLabel={'<'}
                  nextLabel={'>'}
                  breakLabel={'...'}
                  pageCount={totalPages}
                  marginPagesDisplayed={1}
                  pageRangeDisplayed={5}
                  onPageChange={handlePageClick}
                  containerClassName={"flex items-center space-x-2"}
                  activeClassName={"bg-blue-500 text-white rounded-md"}
                  pageClassName={"px-3 py-1 rounded-md hover:bg-gray-200"}
                  previousClassName={"px-3 py-1 rounded-md hover:bg-gray-200"}
                  nextClassName={"px-3 py-1 rounded-md hover:bg-gray-200"}
                />
              </div>
            </div>
          </div>
        </div>
        {editPopup && <EditAffiliateForm editPopup={editPopup} setEditPopup={setEditPopup} />}
      </>
    </Sidebar>
  )
}